import { useEffect } from 'react';
import { useSkillsStore } from '@/stores/skills';
import { clsx } from 'clsx';
import {
  Sparkles,
  ArrowLeft,
  Loader2,
  AlertCircle,
  BookOpen,
  Clapperboard,
  FileText,
  Monitor,
  ArrowRight,
  Workflow,
} from 'lucide-react';

export function CreativeSkillsPage() {
  const { view, setView, loading, error, fetchAll, definitions, frameworks, templates, platforms, pipelines } =
    useSkillsStore();

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <Sparkles size={22} className="text-brand-coral" />
            Creative Skills Engine
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Frameworks, script templates, video platforms, and creative pipelines.
          </p>
        </div>
        {view.kind !== 'overview' && (
          <button
            onClick={() => setView({ kind: 'overview' })}
            className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-navy-900 transition-colors"
          >
            <ArrowLeft size={14} />
            Back to Overview
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {loading && definitions.length === 0 ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Loading skills data...
        </div>
      ) : view.kind === 'overview' ? (
        <OverviewView />
      ) : view.kind === 'skill' ? (
        <SkillDetailView skillId={view.skillId} />
      ) : (
        <TemplateDetailView templateId={view.templateId} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

function OverviewView() {
  const { definitions, frameworks, templates, platforms, pipelines, setView } = useSkillsStore();

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <MiniStat label="Skills" value={definitions.length} icon={Sparkles} color="text-brand-coral" />
        <MiniStat label="Frameworks" value={frameworks.length} icon={BookOpen} color="text-blue-500" />
        <MiniStat label="Templates" value={templates.length} icon={FileText} color="text-purple-500" />
        <MiniStat label="Platforms" value={platforms.length} icon={Monitor} color="text-emerald-500" />
        <MiniStat label="Pipelines" value={pipelines.length} icon={Workflow} color="text-amber-500" />
      </div>

      {/* Skill Definitions */}
      {definitions.length > 0 && (
        <Section title="Skill Definitions" icon={Sparkles} count={definitions.length}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {definitions.map((skill) => (
              <button
                key={skill.skill_id}
                onClick={() => setView({ kind: 'skill', skillId: skill.skill_id })}
                className="bg-slate-50 rounded-lg border border-slate-100 p-4 text-left hover:border-brand-coral/30 hover:bg-white transition-all group"
              >
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-semibold text-navy-900">{skill.name}</h4>
                  <ArrowRight size={12} className="text-slate-300 group-hover:text-brand-coral transition-colors" />
                </div>
                <p className="text-[11px] text-slate-500 line-clamp-2">{skill.description}</p>
                {skill.category && (
                  <span className="inline-block mt-2 text-[9px] font-mono bg-brand-coral/10 text-brand-coral px-1.5 py-0.5 rounded">
                    {skill.category}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Frameworks */}
      {frameworks.length > 0 && (
        <Section title="Creative Frameworks" icon={BookOpen} count={frameworks.length}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {frameworks.map((fw) => (
              <div
                key={fw.id}
                className="bg-white rounded-lg border border-slate-200 p-4"
              >
                <h4 className="text-sm font-semibold text-navy-900 mb-1">{fw.name}</h4>
                <p className="text-[11px] text-slate-500 line-clamp-2 mb-2">{fw.description}</p>
                <div className="flex flex-wrap gap-1">
                  {fw.archetype && (
                    <span className="text-[9px] font-mono bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded border border-blue-100">
                      {fw.archetype}
                    </span>
                  )}
                  {fw.funnel_stages?.map((s) => (
                    <span
                      key={s}
                      className="text-[9px] font-mono bg-slate-50 text-slate-500 px-1.5 py-0.5 rounded border border-slate-100"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Script Templates */}
      {templates.length > 0 && (
        <Section title="Script Templates" icon={Clapperboard} count={templates.length}>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-left">
                  <th className="px-4 py-2.5 font-semibold text-navy-900">Name</th>
                  <th className="px-4 py-2.5 font-semibold text-navy-900">Type</th>
                  <th className="px-4 py-2.5 font-semibold text-navy-900">Segments</th>
                  <th className="px-4 py-2.5 font-semibold text-navy-900">Duration</th>
                  <th className="px-4 py-2.5 font-semibold text-navy-900">Style</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr
                    key={t.id}
                    className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => setView({ kind: 'template', templateId: t.id })}
                  >
                    <td className="px-4 py-2.5 font-medium text-navy-900">{t.name}</td>
                    <td className="px-4 py-2.5">
                      <span className="font-mono bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded text-[9px]">
                        {t.script_type}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">{t.segment_count ?? '—'}</td>
                    <td className="px-4 py-2.5 text-slate-500">
                      {t.total_duration_seconds ? `${t.total_duration_seconds}s` : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">{t.visual_style ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Video Platforms */}
      {platforms.length > 0 && (
        <Section title="Video Platforms" icon={Monitor} count={platforms.length}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {platforms.map((p) => (
              <div
                key={p.id}
                className="bg-white rounded-lg border border-slate-200 p-4"
              >
                <h4 className="text-sm font-semibold text-navy-900 mb-1">{p.name}</h4>
                <span className="text-[9px] font-mono bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded border border-emerald-100">
                  {p.type}
                </span>
                {p.capabilities && p.capabilities.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.capabilities.map((c) => (
                      <span
                        key={c}
                        className="text-[9px] bg-slate-50 text-slate-500 px-1.5 py-0.5 rounded border border-slate-100"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Pipelines */}
      {pipelines.length > 0 && (
        <Section title="Generation Pipelines" icon={Workflow} count={pipelines.length}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {pipelines.map((pipe) => (
              <div
                key={pipe.id}
                className="bg-white rounded-lg border border-slate-200 p-4"
              >
                <h4 className="text-sm font-semibold text-navy-900 mb-1">{pipe.name}</h4>
                {pipe.description && (
                  <p className="text-[11px] text-slate-500 mb-2">{pipe.description}</p>
                )}
                {pipe.steps && pipe.steps.length > 0 && (
                  <div className="flex items-center gap-1 text-[9px] font-mono text-slate-400">
                    {pipe.steps.map((s, i) => (
                      <span key={s} className="flex items-center gap-1">
                        <span className="bg-amber-50 text-amber-600 px-1 py-0.5 rounded border border-amber-100">
                          {s}
                        </span>
                        {i < pipe.steps!.length - 1 && <ArrowRight size={8} className="text-slate-300" />}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skill Detail
// ---------------------------------------------------------------------------

function SkillDetailView({ skillId }: { skillId: string }) {
  const { fetchSkillDetail, selectedSkill, loading } = useSkillsStore();

  useEffect(() => {
    fetchSkillDetail(skillId);
  }, [skillId, fetchSkillDetail]);

  if (loading || !selectedSkill) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
        <Loader2 size={16} className="animate-spin" />
        Loading skill...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-navy-900">{selectedSkill.name}</h2>
        <p className="text-sm text-slate-500 mt-1">{selectedSkill.description}</p>
        {selectedSkill.category && (
          <span className="inline-block mt-2 text-[10px] font-mono bg-brand-coral/10 text-brand-coral px-2 py-0.5 rounded">
            {selectedSkill.category}
          </span>
        )}
      </div>

      {selectedSkill.input_schema && (
        <div>
          <h3 className="text-sm font-semibold text-navy-900 mb-2">Input Schema</h3>
          <pre className="bg-slate-50 border border-slate-100 rounded-lg p-4 text-[11px] font-mono text-slate-600 overflow-auto max-h-60">
            {JSON.stringify(selectedSkill.input_schema, null, 2)}
          </pre>
        </div>
      )}

      {selectedSkill.output_schema && (
        <div>
          <h3 className="text-sm font-semibold text-navy-900 mb-2">Output Schema</h3>
          <pre className="bg-slate-50 border border-slate-100 rounded-lg p-4 text-[11px] font-mono text-slate-600 overflow-auto max-h-60">
            {JSON.stringify(selectedSkill.output_schema, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template Detail (placeholder)
// ---------------------------------------------------------------------------

function TemplateDetailView({ templateId }: { templateId: string }) {
  const { templates } = useSkillsStore();
  const template = templates.find((t) => t.id === templateId);

  if (!template) {
    return (
      <div className="text-center py-16 text-slate-400 text-sm">
        Template not found.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-navy-900">{template.name}</h2>
        {template.description && (
          <p className="text-sm text-slate-500 mt-1">{template.description}</p>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <DetailField label="Script Type" value={template.script_type} />
        <DetailField label="Segments" value={template.segment_count?.toString() ?? '—'} />
        <DetailField
          label="Duration"
          value={template.total_duration_seconds ? `${template.total_duration_seconds}s` : '—'}
        />
        <DetailField label="Visual Style" value={template.visual_style ?? '—'} />
      </div>

      {template.audio_style && (
        <DetailField label="Audio Style" value={template.audio_style} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function MiniStat({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="stat-card">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono uppercase text-slate-400 tracking-wider">
          {label}
        </span>
        <Icon size={14} className={color} />
      </div>
      <p className="text-lg font-bold text-navy-900">{value}</p>
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  count,
  children,
}: {
  title: string;
  icon: React.ElementType;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-slate-400" />
        <h3 className="font-semibold text-navy-900 text-sm">{title}</h3>
        <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
          {count}
        </span>
      </div>
      {children}
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-[10px] font-mono text-slate-400 uppercase">{label}</span>
      <p className="text-sm font-medium text-navy-900 mt-0.5">{value}</p>
    </div>
  );
}
